/**
 * Shared class overview (stats, filters, students table) for admin View Class
 * and teacher dashboard /dashboard/my-classes/:classId.
 * Class header counts (active / inactive / level test / reset password) load from
 * `admin-management/.../groups/details/:groupId` for both roles; teachers fall back to
 * report search + students report only if that request fails.
 * Students table: students report search (server mode) for both.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import classnames from 'classnames';
import SVG from 'react-inlinesvg';
import arrowLeft from '@/assets/svg/arrowLeft.svg';
import { classService } from '@/school-management/services/class';
import { Class } from '@/school-management/types/response';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { errorSelector } from '@/store/reducers/error';
import Button from '@/components/school-management/Button/Button';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import SkeltonLoader from '@/components/school-management/SkeltonLoader/SkeltonLoader';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import commonUtils from '@/school-management/utils/common';

import {
  FilterField,
  FilterValue,
  isEmptyValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import Students from '@/components/school-management/SchoolDetailsPage/Students';
import coStyles from './ClassOverview.module.scss';
import { adminManagementService } from '@/admin/services/adminManagement';
import { UserStatisticsResponseData } from '@/school-management/types/response';
import { buildUserStatisticsPayload } from '@/school-management/utils/userStatisticsFilters';
import { formatMinutes } from '@/utils/formatters';
import { createAnchorWrapper } from '@/components/school-management/Anchor/Anchor';
import UseScreenSize from '@/hooks/UseScreenSize';
import SectionSeperator from '@/components/SectionSeperator/SectionSeperator';
import scCards from '@/components/StudentsData/StudentsCards/StudentsCards.module.scss';
import downloadListIcon from '@/assets/svg/downloadList.svg';
import clockIcon from '@/assets/svg/clock.svg';
import medalIcon from '@/assets/svg/medal.svg';
import avgTimeIcon from '@/assets/svg/avgTime.svg';
import progressIcon from '@/assets/svg/progress.svg';
import victoryTrophyIcon from '@/assets/svg/victory-trophy.svg';
import PageHeaderFiltersBar from '@/components/school-management/GenericTable/PageHeader/PageHeaderFiltersBar';
import type {
  CascadingFilterRequest,
  StudentAutocompleteItem,
  GroupAutocompleteItem,
} from '@/school-management/types/response/autocomplete';
import { registerService } from '@/services/register';
import { fetchAllStudentsReportItemsForGroup } from '@/school-management/services/student';
import {
  buildClassFromGroupReportItem,
  buildClassFromStudentReportItems,
} from '@/components/ClassOverview/teacherOverviewClass';
import ClassModalsManager from '@/components/school-management/SchoolDetailsPage/ClassModalsManager';
import ClassCodeModal from '@/components/school-management/ClassCodeModal/ClassCodeModal';
import FullClassModal from '@/components/FullClassModal/FullClassModal';
import DealLimitReachedMessage from '@/components/DealLimitReachedMessage/DealLimitReachedMessage';
import ClassReport from '@/components/school-management/StudentDataModal/GenerateReport/ClassReport';
import AppModal from '@/components/AppModal/AppModal';
import ReportErrorModal from '@/components/school-management/StudentDataModal/GenerateReport/ReportErrorModal';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import config from '@/config';
import BackButton from '@/components/BackButton/BackButton';
import { EventDispatcherContext, DispatcherEvents } from '@/context/eventDispatcherContext';

const DEFAULT_STUDENT_FILTERS: Record<string, FilterValue> = {
  name: [],
  groupName: [],
  startingLevel: [],
  currentLevel: [],
  status: [],
  completedActivities: {},
  score: {},
  firstLogin: { mode: 'none' },
  lastLogin: { mode: 'none' },
  displayDataFor: 'currentLevel',
};

function teacherFullName(teach: { firstName: string; lastName: string }): string {
  return `${teach.firstName} ${teach.lastName}`.trim();
}

export interface ClassOverviewProps {
  classId: string;
  organizationId: string;
  canEditClass: boolean;
  onBack: () => void;
  onEditClass?: () => void;
  /** If class API returns no teachers, show this (e.g. current teacher on dashboard). */
  fallbackTeacherName?: string;
  /**
   * `admin` vs `teacher` affects header teacher/co-teacher lines, edit affordances, and modals.
   * Class payload is loaded from `groups/details` for both; teacher mode keeps a legacy fallback if that fails.
   */
  dataSource?: 'admin' | 'teacher';
}

interface AvgViewData {
  type: string;
  icon: string;
  value: string;
  label: string;
  tooltip?: string;
  button?: React.ReactNode;
}

const TRANSLATION_NS = 'translation';

const ClassOverview: React.FC<ClassOverviewProps> = ({
  classId,
  organizationId: apiSchoolId,
  canEditClass,
  onBack,
  onEditClass,
  fallbackTeacherName,
  dataSource = 'admin',
}) => {
  const { user } = useAppSelector(authSelector);
  const errorState = useAppSelector(errorSelector);
  const { t, i18n } = useAdminTranslation();
  const { isDesktop, isMobile } = UseScreenSize();
  const SpanAnchor = useMemo(() => createAnchorWrapper('span'), []);
  const studentExportHandlerRef = useRef<(() => void) | null>(null);

  const [classData, setClassData] = useState<Class | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const toolsRef = useRef<HTMLDivElement>(null);
  const [, setShowStudentsStatistics] = useState(false);
  const [studentFilters, setStudentFiltersInternal] = useState<Record<string, FilterValue>>(() => ({
    ...DEFAULT_STUDENT_FILTERS,
  }));
  const [studentsRefreshKey, setStudentsRefreshKey] = useState(0);
  const [dashboardStats, setDashboardStats] = useState<UserStatisticsResponseData | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [showStudentUploadModal, setShowStudentUploadModal] = useState(false);
  const [showFullClassModal, setShowFullClassModal] = useState(false);
  const [showClassCodeModal, setShowClassCodeModal] = useState(false);
  const [modalClassCode, setModalClassCode] = useState('');
  const [selectedClassDealId, setSelectedClassDealId] = useState('');
  const [showDealLimitReachedModal, setShowDealLimitReachedModal] = useState(false);
  const [showClassReport, setShowClassReport] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [isServerError, setIsServerError] = useState(false);
  const [isErrorDownloading, setIsErrorDownloading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('success');
  const [, setShowUsersCreateError] = useState(false);
  const [hasSelectedStudents, setHasSelectedStudents] = useState<boolean>(false);
  const eventDispatcherContext = useContext(EventDispatcherContext);

  const loadTeacherClassFromApis = useCallback(async (): Promise<Class | null> => {
    if (!apiSchoolId || !classId) return null;
    try {
      const groupsRes = await classService.getAllClasses(apiSchoolId, {
        groupIds: [classId],
        page: 1,
        pageSize: Math.max(20, config.groupsPageSize),
      });
      const match =
        groupsRes.data?.items?.find(g => g.groupId === classId) ?? groupsRes.data?.items?.[0];
      if (match) {
        return buildClassFromGroupReportItem(match, classId);
      }
    } catch {
      /* fall through */
    }
    try {
      const items = await fetchAllStudentsReportItemsForGroup(apiSchoolId, classId);
      return buildClassFromStudentReportItems(classId, items, user ?? null, fallbackTeacherName);
    } catch {
      return null;
    }
  }, [apiSchoolId, classId, user, fallbackTeacherName]);

  const resolveClassData = useCallback(async (): Promise<Class | null> => {
    if (!apiSchoolId || !classId) return null;
    try {
      const response = await classService.getClassById(apiSchoolId, classId);
      if (response.data?.status === 'success' && response.data?.data) {
        return response.data.data as Class;
      }
    } catch {
      /* try teacher fallback below */
    }
    if (dataSource === 'teacher' && user) {
      return loadTeacherClassFromApis();
    }
    return null;
  }, [apiSchoolId, classId, dataSource, user, loadTeacherClassFromApis]);

  const refetchClassDetails = useCallback(async () => {
    if (!classId || !apiSchoolId) return;
    try {
      eventDispatcherContext.dispatch(DispatcherEvents.SCHOOL_DETAILS_CHANGE, null);
      const data = await resolveClassData();
      if (data) setClassData(data);
    } catch {
      /* keep existing */
    }
  }, [classId, apiSchoolId, resolveClassData, eventDispatcherContext]);

  useEffect(() => {
    if (!classId || !apiSchoolId) {
      setIsLoading(false);
      setLoadError(true);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    const run = async () => {
      try {
        const data = await resolveClassData();
        if (cancelled) return;
        if (!data) {
          setLoadError(true);
          setClassData(null);
          return;
        }
        setClassData(data);
        setStudentFiltersInternal({
          ...DEFAULT_STUDENT_FILTERS,
          groupName: [
            {
              groupId: data.groupId || classId,
              groupName: (data.groupName || '').trim(),
              teachers: [],
            },
          ],
        });
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setClassData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [apiSchoolId, classId, resolveClassData]);

  const effectiveGroupId = classData?.groupId || classId || '';
  const displayClassName = (classData?.groupName || '').trim();

  const lockedGroup = useMemo(
    () => [
      {
        groupId: effectiveGroupId,
        groupName: displayClassName,
        teachers: [] as [],
      },
    ],
    [effectiveGroupId, displayClassName]
  );

  const setStudentFilters = useCallback(
    (next: React.SetStateAction<Record<string, FilterValue>>) => {
      setStudentFiltersInternal(prev => {
        const resolved =
          typeof next === 'function' ? (next as (p: typeof prev) => typeof prev)(prev) : next;
        return {
          ...resolved,
          groupName: lockedGroup,
        };
      });
    },
    [lockedGroup]
  );

  const toGroupIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as GroupAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.groupId) : undefined;
  };

  const toStudentIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as StudentAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.id) : undefined;
  };

  const classStudentCascadingFilters = useMemo(
    (): CascadingFilterRequest => ({
      groupIds: toGroupIds(studentFilters.groupName),
      studentIds: toStudentIds(studentFilters.name),
    }),
    [studentFilters]
  );

  const statsPayload = useMemo(() => {
    const payload = buildUserStatisticsPayload({
      activeTab: 'students',
      studentFilters,
      t,
    });
    if (effectiveGroupId) {
      const existing = payload.groupIds ?? [];
      if (!existing.includes(effectiveGroupId)) {
        payload.groupIds = [...existing, effectiveGroupId];
      }
    }
    return payload;
  }, [studentFilters, effectiveGroupId, t]);

  const isActive = useRef<boolean>(false);
  useEffect(() => {
    isActive.current = true;

    return () => {
      isActive.current = false;
    };
  }, []);

  const loadDashboardStatsKey = useRef<number>(0);
  const loadDashboardStats = useCallback(() => {
    if (!effectiveGroupId || !isActive.current) return;

    loadDashboardStatsKey.current = loadDashboardStatsKey.current + 1;
    const loadKey = loadDashboardStatsKey.current;

    const isCancelled = () => loadKey !== loadDashboardStatsKey.current || !isActive.current;

    setStatsLoading(true);
    adminManagementService
      .getUserStatistics(statsPayload)
      .then(res => {
        if (!isCancelled() && res?.data?.data) {
          setDashboardStats(res.data.data);
        }
      })
      .finally(() => {
        if (!isCancelled()) setStatsLoading(false);
      });
  }, [effectiveGroupId, statsPayload, studentsRefreshKey]);

  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  useEffect(() => {
    if (!errorState.show && loadingReport) {
      setLoadingReport(false);
    }
  }, [errorState.show, loadingReport]);

  const viewClassFilterFields: FilterField[] = useMemo(() => {
    const startingLevels = Array.from({ length: 5 }, (_, i) => String(i + 1));
    const currentLevels = Array.from({ length: 5 }, (_, i) => String(i + 1));
    const userStatus = [t('active'), t('inactive'), t('levelTestIncomplete')];
    return [
      {
        key: 'name',
        label: t('studentMetricsDashboard.filters.studentName') || 'Student Name',
        options: [],
        multi: true,
        autocompleteType: 'students',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.studentNameSearchPlaceholder') || 'Search student',
      },
      {
        key: 'startingLevel',
        label: t('studentMetricsDashboard.filters.startingLevel') || 'Starting Level',
        options: startingLevels,
        multi: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.startingLevelSearchPlaceholder') || 'Search level',
      },
      {
        key: 'currentLevel',
        label: t('studentMetricsDashboard.filters.currentLevel') || 'Current Level',
        options: currentLevels,
        multi: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.currentLevelSearchPlaceholder') || 'Search level',
      },
      {
        key: 'status',
        label: t('studentMetricsDashboard.filters.status') || 'Status',
        options: userStatus,
        multi: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.statusSearchPlaceholder') || 'Search status',
      },
      {
        key: 'completedActivities',
        label: t('studentMetricsDashboard.filters.completedActivities') || 'Completed Activities',
        options: [],
        numberRangeMode: { min: 0 },
      },
      {
        key: 'score',
        label: t('studentMetricsDashboard.filters.score') || 'Score',
        options: [],
        numberRangeMode: {
          min: 0,
          max: 100,
        },
      },
      {
        key: 'firstLogin',
        label: t('studentMetricsDashboard.filters.firstLogin') || 'First Login',
        options: ['last7', 'last30', 'specific', 'range'] as string[],
        dateMode: true,
      },
      {
        key: 'lastLogin',
        label: t('studentMetricsDashboard.filters.lastLogin') || 'Last Login',
        options: ['last7', 'last30', 'specific', 'range'] as string[],
        dateMode: true,
      },
    ];
  }, [t]);

  // EN-only " Grade" suffix for K–12 ordinals
  const gradeLabel = useMemo(() => {
    if (!classData) return '';
    let label = commonUtils.getClassGradeDisplayLabel(classData, t);
    if (label === '—' || !label) return label;
    const lang = (i18n.resolvedLanguage || i18n.language || '').toLowerCase();
    if (lang.startsWith('en')) {
      const param = commonUtils.getClassGradeParamForReport(classData, t);
      const n = parseInt(param, 10);
      if (!Number.isNaN(n) && n >= 1 && n <= 14) label = `Grade: ${label}`;
    }
    return label;
  }, [classData, t, i18n.language, i18n.resolvedLanguage]);

  const gradeIdForReport = useMemo(
    () => (classData ? commonUtils.getClassGradeParamForReport(classData, t) : ''),
    [classData, t]
  );

  const mainTeacher = classData?.teachers?.[0];
  const coTeacher = classData?.teachers?.[1];
  const primaryTeacherLabel =
    dataSource === 'admin'
      ? mainTeacher
        ? teacherFullName(mainTeacher)
        : (fallbackTeacherName || '').trim() || null
      : null;

  /** Teacher dashboard: only show other teachers (co-teachers); hide when alone (that's the current user). */
  const coTeachersForTeacherView = useMemo(() => {
    if (dataSource !== 'teacher' || !classData?.teachers?.length) return [];
    const list = classData.teachers;
    if (list.length <= 1) return [];
    const uid = user?.metadata?.id;
    if (uid) {
      return list.filter(t => t.id !== uid);
    }
    return list.slice(1);
  }, [dataSource, classData?.teachers, user?.metadata?.id]);

  const hasClassStudentFilter = useMemo(
    () => viewClassFilterFields.some(f => !isEmptyValue(f, studentFilters[f.key])),
    [viewClassFilterFields, studentFilters]
  );

  const handleAddStudents = async () => {
    if (!classId || !classData) return;
    let isAllowed = false;
    try {
      isAllowed = !(await registerService.isRegistrationLimitReached(user))?.data.data;
    } catch {
      setShowDealLimitReachedModal(true);
      return;
    }
    if (!isAllowed) {
      setShowDealLimitReachedModal(true);
      return;
    }

    if (classData?.classCode) {
      setModalClassCode(classData.classCode);
      setShowClassCodeModal(true);
      return;
    }

    const count = classData?.studentCount ?? 0;
    if (count >= config.maxStudentsPerClass) {
      setShowFullClassModal(true);
      return;
    }

    const userDealId = user?.metadata?.organizationDetails?.dealId;
    if (userDealId) {
      setSelectedClassDealId(userDealId);
    } else {
      try {
        const response = await classService.getClassById(apiSchoolId, classId);
        const dealId = (response.data?.data as { dealId?: string })?.dealId;
        setSelectedClassDealId(dealId && response.data?.status === 'success' ? dealId : '');
      } catch {
        setSelectedClassDealId('');
      }
    }
    setShowStudentUploadModal(true);
  };

  const handleDownloadClassReport = () => {
    if (!classData) return;
    setLoadingReport(true);
    setShowClassReport(true);
  };

  const onLoadReportError = useCallback(() => {
    setIsServerError(true);
    setLoadingReport(false);
  }, []);

  const closeErrorModal = () => {
    setIsServerError(false);
    setIsErrorDownloading(false);
    setLoadingReport(false);
    setShowClassReport(false);
  };

  const handleReportGenerated = () => {
    setShowClassReport(false);
    setLoadingReport(false);
  };

  const classStatisticsRow = useMemo(() => {
    const statsCardsLoading = isLoading || statsLoading;

    const getAvgTooltip = (labelKey: string): string => {
      const tooltipKey = `${labelKey}Tooltip`;
      return i18n.exists(tooltipKey, { ns: TRANSLATION_NS })
        ? String(t(tooltipKey, { ns: TRANSLATION_NS }))
        : '';
    };

    const createAvgView = (viewData: AvgViewData | null) =>
      viewData && (
        <div key={viewData.type} className={classnames(scCards.avgData, coStyles.statCard)}>
          <div className={scCards.avgDataValue}>
            {statsCardsLoading ? (
              <SkeltonLoader className={coStyles.statSkeletonValue} />
            ) : (
              viewData.value
            )}
          </div>
          <div
            className={classnames(scCards.avgDataLabel, {
              [scCards.avgDataButton]: !!viewData.button && !statsCardsLoading,
            })}
          >
            {statsCardsLoading ? (
              <SkeltonLoader className={coStyles.statSkeletonLabel} />
            ) : (
              viewData.button || (
                <>
                  <span className={scCards.avgDataLabelIcon} aria-hidden='true'>
                    <SVG src={viewData.icon} />
                  </span>
                  <span>
                    {t(String(isMobile ? `${viewData.label}_mobile` : viewData.label), {
                      ns: TRANSLATION_NS,
                    })}
                  </span>
                  {viewData.tooltip ? (
                    <SpanAnchor
                      className={scCards.avgHelp}
                      trigerType='hover'
                      autoClose={false}
                      stayInView={true}
                      tooltip={<div className={scCards.avgTooltip}>{viewData.tooltip}</div>}
                      renderTooltipPosition={anchorPosition => {
                        return {
                          top: anchorPosition.top + anchorPosition.height + 5,
                          left: anchorPosition.left + anchorPosition.width + 5,
                        };
                      }}
                    >
                      <AppIcon icon='helpBtn' size={14} />
                    </SpanAnchor>
                  ) : null}
                </>
              )
            )}
          </div>
        </div>
      );

    const cards: {
      type: string;
      icon: string;
      label: string;
      getValue: () => number | undefined | null;
      formatValue: (value: number) => string;
    }[] = [
      {
        type: 'currentLevel',
        icon: victoryTrophyIcon,
        label: 'avgCurrentLevel',
        getValue: () => dashboardStats?.averageCurrentLevel,
        formatValue: (value: number) => `${Math.round((value + Number.EPSILON) * 10) / 10}`,
      },
      {
        type: 'score',
        icon: medalIcon,
        label: 'avgScore',
        getValue: () => dashboardStats?.averageScore,
        formatValue: (value: number) => `${Math.round(value)}`,
      },
      {
        type: 'progress',
        icon: progressIcon,
        label: 'avgProgress',
        getValue: () => dashboardStats?.averageProgress,
        formatValue: (value: number) => `${Math.round(value)}%`,
      },
      {
        type: 'totalTime',
        icon: clockIcon,
        label: 'totalTimeSpent',
        getValue: () => dashboardStats?.totalTimeSpent,
        formatValue: (value: number) => formatMinutes(value),
      },
      {
        type: 'avgTime',
        icon: avgTimeIcon,
        label: 'avgTimeSpent',
        getValue: () => dashboardStats?.averageTimeSpent,
        formatValue: (value: number) => formatMinutes(value),
      },
    ];

    return cards.map(avg => {
      const rawValue = statsCardsLoading ? null : avg.getValue();
      const value = rawValue !== undefined && rawValue !== null ? avg.formatValue(rawValue) : '-';
      const viewData: AvgViewData = {
        type: avg.type,
        icon: avg.icon,
        value,
        label: avg.label,
        tooltip: getAvgTooltip(avg.label),
      };
      return createAvgView(viewData);
    });
  }, [dashboardStats, statsLoading, isLoading, t, i18n, isMobile, SpanAnchor]);

  if (!isLoading && (loadError || !classData)) {
    return (
      <div className={coStyles.root}>
        <div className={coStyles.errorWrap}>
          <h2>{t('schoolDetailsPage.classes.classNotFound')}</h2>
          <Button onClick={onBack}>{t('schoolDetailsPage.classes.backToClasses')}</Button>
        </div>
      </div>
    );
  }

  const totalStudents = classData?.studentCount ?? 0;
  const activeStudents = classData?.activeStudentsCount ?? 0;
  const levelTestIncompleteStudentsCount = classData?.levelTestIncompleteStudentsCount ?? 0;
  const resetPasswordStudentsCount = classData?.resetPasswordStudentsCount ?? 0;
  const inactiveStudents = Math.max(
    0,
    totalStudents - activeStudents - levelTestIncompleteStudentsCount - resetPasswordStudentsCount
  );

  return (
    <div className={coStyles.root}>
      <BackButton onClick={onBack} />
      <div className={coStyles.heroRow}>
        <div className={coStyles.heroLeft}>
          <h1 className={coStyles.classTitle}>
            {displayClassName ? (
              displayClassName
            ) : isLoading ? (
              <span className={coStyles.classTitleSkeletonWrap}>
                <SkeltonLoader className={coStyles.classTitleSkeleton} aria-hidden />
              </span>
            ) : null}
          </h1>
          {gradeLabel !== '—' ? <span className={coStyles.gradePill}>{gradeLabel}</span> : null}
          {canEditClass && onEditClass && (
            <button
              type='button'
              className={coStyles.editGradeBtn}
              title={String(t('schoolDetailsPage.classes.viewClassPage.editClass'))}
              onClick={onEditClass}
            >
              <AppIcon icon='pencil' width={18} height={18} />
            </button>
          )}
        </div>
        <div className={coStyles.heroActions}>
          <PageHeaderFiltersBar
            filterFields={viewClassFilterFields}
            filters={studentFilters}
            setFilters={setStudentFilters}
            organizationId={apiSchoolId}
            cascadingFilters={classStudentCascadingFilters}
            disabled={hasSelectedStudents}
          />
          <Button className={coStyles.heroPrimaryButton} onClick={handleAddStudents}>
            {t('schoolDetailsPage.classes.viewClassPage.addStudents')}
          </Button>
        </div>
      </div>

      <div className={coStyles.metaRow}>
        <span className={coStyles.metaItem}>
          <span className={coStyles.metaLabel}>
            {t('schoolDetailsPage.classes.viewClassPage.metaStudentsLabel')}:
          </span>
          <span className={coStyles.metaValue}>
            {' '}
            {totalStudents}/{config.maxStudentsPerClass}
          </span>
        </span>
        <span className={coStyles.metaSep}>|</span>
        <span className={coStyles.metaItem}>
          <span className={coStyles.metaLabel}>
            {t('schoolDetailsPage.classes.viewClassPage.metaActiveLabel')}:
          </span>
          <span className={coStyles.metaValue}> {activeStudents}</span>
        </span>
        {!classData?.classCode ? (
          <>
            <span className={coStyles.metaSep}>|</span>
            <span className={coStyles.metaItem}>
              <span className={coStyles.metaLabel}>
                {t('schoolDetailsPage.classes.viewClassPage.metaInactiveLabel')}:
              </span>
              <span className={coStyles.metaValue}> {inactiveStudents}</span>
            </span>
          </>
        ) : null}

        {levelTestIncompleteStudentsCount > 0 && (
          <>
            <span className={coStyles.metaSep}>|</span>
            <span className={coStyles.metaItem}>
              <span className={coStyles.metaLabel}>{t('levelTestIncomplete')}:</span>
              <span className={coStyles.metaValue}> {levelTestIncompleteStudentsCount}</span>
            </span>
          </>
        )}

        {resetPasswordStudentsCount > 0 && (
          <>
            <span className={coStyles.metaSep}>|</span>
            <span className={coStyles.metaItem}>
              <span className={coStyles.metaLabel}>{t('resetPassword')}:</span>
              <span className={coStyles.metaValue}> {resetPasswordStudentsCount}</span>
            </span>
          </>
        )}

        {dataSource === 'teacher' ? (
          coTeachersForTeacherView.length > 0 ? (
            <>
              <span className={coStyles.metaSep}>|</span>
              <span className={coStyles.metaItem}>
                <span className={coStyles.metaLabel}>
                  {t('schoolDetailsPage.classes.viewClassPage.coTeacher')}:
                </span>
                <span className={coStyles.metaValue}>
                  {' '}
                  {coTeachersForTeacherView.map(teacherFullName).join(', ')}
                </span>
              </span>
            </>
          ) : null
        ) : (
          <>
            {primaryTeacherLabel ? (
              <>
                <span className={coStyles.metaSep}>|</span>
                <span className={coStyles.metaItem}>
                  <span className={coStyles.metaLabel}>
                    {t('schoolDetailsPage.classes.viewClassPage.teacher')}:
                  </span>
                  <span className={coStyles.metaValue}> {primaryTeacherLabel}</span>
                </span>
              </>
            ) : null}
            {coTeacher ? (
              <>
                <span className={coStyles.metaSep}>|</span>
                <span className={coStyles.metaItem}>
                  <span className={coStyles.metaLabel}>
                    {t('schoolDetailsPage.classes.viewClassPage.coTeacher')}:
                  </span>
                  <span className={coStyles.metaValue}> {teacherFullName(coTeacher)}</span>
                </span>
              </>
            ) : null}
          </>
        )}
        {classData?.classCode ? (
          <>
            <span className={coStyles.metaSep}>|</span>
            <span className={coStyles.metaItem}>
              <span className={coStyles.metaLabel}>
                {t('schoolDetailsPage.classes.viewClassPage.classCodeLabel')}:
              </span>{' '}
              <code className={coStyles.inlineCode}>{classData?.classCode}</code>
            </span>
          </>
        ) : null}
      </div>

      <div className={coStyles.downloadAndDisplayRow}>
        <div ref={toolsRef} className={coStyles.displayDataSlot} />
        <div className={coStyles.downloadRow}>
          <button
            type='button'
            className={coStyles.downloadLink}
            onClick={handleDownloadClassReport}
          >
            {t('schoolDetailsPage.classes.actions.downloadReport')}
          </button>
        </div>
      </div>

      <div className={coStyles.classStatsSection}>
        <div className={scCards.studentsCards}>
          <div className={scCards.studentsStatistics}>{classStatisticsRow}</div>
        </div>
      </div>

      <div className={coStyles.studentsWrap}>
        <Students
          key={studentsRefreshKey}
          schoolId={apiSchoolId}
          studentFilters={studentFilters}
          setStudentFilters={setStudentFilters}
          hasAnyFilter={hasClassStudentFilter}
          toolsContainer={toolsRef}
          setShowStudentsStatistics={setShowStudentsStatistics}
          columnsVariant='classView'
          studentExportHandlerRef={studentExportHandlerRef}
          studentsDataMode='server'
          onClientDataInvalidate={() => setStudentsRefreshKey(k => k + 1)}
          onStudentsSelected={(selectedStudents: any[]) => {
            setHasSelectedStudents(selectedStudents.length > 0);
          }}
          onRefreshData={() => {
            loadDashboardStats();
            refetchClassDetails();
          }}
        />
      </div>

      <DealLimitReachedMessage
        show={showDealLimitReachedModal}
        onClose={() => setShowDealLimitReachedModal(false)}
      />
      <ClassCodeModal
        open={showClassCodeModal}
        classCode={modalClassCode}
        onClose={() => setShowClassCodeModal(false)}
      />
      <FullClassModal show={showFullClassModal} onClose={() => setShowFullClassModal(false)} />
      <ClassModalsManager
        showUploadModal={showStudentUploadModal}
        setShowUploadModal={setShowStudentUploadModal}
        addUsersToNewEntity={false}
        setAddUsersToNewEntity={() => {}}
        onUsersCreate={() => {}}
        setShowUsersCreateError={setShowUsersCreateError}
        groupId={classId || ''}
        groupName={displayClassName}
        dealId={selectedClassDealId}
        isTeacher={false}
        usersInClass={totalStudents}
        onStayPageStudentAddComplete={addedUsers => {
          if (addedUsers > 0) {
            setNotifMessage(t('schoolsListPage.studentCreatedSuccessfully') || '');
            setNotifType('success');
            setNotifOpen(true);
          }
          setStudentsRefreshKey(k => k + 1);
          void refetchClassDetails();
        }}
      />

      {loadingReport && (
        <AppModal show showBackdrop={false} blockBodyScroll>
          <AppLoaderCircle />
        </AppModal>
      )}
      <ReportErrorModal
        show={isErrorDownloading || isServerError}
        onClose={closeErrorModal}
        isStudentReport={false}
      />
      {showClassReport && classId && (
        <ClassReport
          schoolId={apiSchoolId}
          classId={classId}
          className={displayClassName}
          classGrade={gradeIdForReport}
          totalStudents={totalStudents}
          onLoadReportError={onLoadReportError}
          onReportGenerated={handleReportGenerated}
        />
      )}
      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => setNotifOpen(false)}
      />
    </div>
  );
};

export default ClassOverview;
